import { useEffect } from "react";
import { Link } from "react-router-dom";
import MarkdownContent from "@/components/ui/MarkdownContent";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone } from "lucide-react";
import { getOptimizedImageUrl, getResponsiveAvifSrcSet, getResponsiveWebpSrcSet, getResponsiveSrcSet } from "@/lib/imageUtils";
import { useGlobalSettings } from "@/hooks/useGlobalSettings";

interface HomepageIntroSectionProps {
  title: string;
  contentAbove?: string;
  contentBeside?: string;
  image?: string;
  imageAlt?: string;
  className?: string;
  imagePosition?: 'left' | 'right';
  ctaText?: string;
  ctaLink?: string;
  backgroundColor?: string;
  textColor?: string;
}

const HomepageIntroSection = ({ 
  title, 
  contentAbove,
  contentBeside,
  image,
  imageAlt,
  className = "",
  imagePosition = 'right',
  ctaText,
  ctaLink,
  backgroundColor,
  textColor,
}: HomepageIntroSectionProps) => {
  const { data: settings } = useGlobalSettings();
  const phone = settings?.phone;
  const textStyle = textColor ? { color: textColor } : undefined;
  // Hide the HTML placeholder when React mounts to prevent LCP replacement flash
  useEffect(() => {
    const placeholder = document.getElementById('intro-placeholder');
    if (placeholder) {
      placeholder.classList.remove('intro-placeholder-visible');
      placeholder.classList.add('intro-placeholder-hidden');
    }
  }, []);

  const rawTitle = (title ?? "").trim();
  let displayTitle = rawTitle;
  let displayContentAbove = contentAbove;

  // If the title was entered inside the CMS rich text as a markdown heading (e.g. "## Title"),
  // promote it to the real section title so it uses the correct heading styles.
  if (!displayTitle && displayContentAbove) {
    const lines = displayContentAbove.split("\n");
    const firstNonEmptyIndex = lines.findIndex((l) => l.trim().length > 0);

    if (firstNonEmptyIndex !== -1) {
      const firstLine = lines[firstNonEmptyIndex].trim();
      if (firstLine.startsWith("## ")) {
        displayTitle = firstLine.replace(/^##\s+/, "").trim();
        lines.splice(firstNonEmptyIndex, 1);
        displayContentAbove = lines.join("\n").trimStart();
      }
    }
  }

  const hasImage = !!image;
  const hasText = Boolean((displayContentAbove ?? "").trim() || (contentBeside ?? "").trim());

  if (!hasText && !hasImage) return null;

  return (
    <section 
      className={`py-8 md:py-12 ${!backgroundColor ? 'bg-muted/30' : ''} ${className}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      <div className="container-wide">
        <article className="bg-background rounded-xl shadow-sm border border-border/50 px-6 md:px-10 pt-4 md:pt-6 pb-6 md:pb-10">
          {displayTitle && (
            <h2 className="heading-section text-foreground mb-6" style={textStyle}>
              {displayTitle}
            </h2>
          )}
          
          {/* Full-width content - appears ABOVE the image */}
          {displayContentAbove && (
            <div className="prose prose-lg max-w-none text-muted-foreground mb-6 [&>*:first-child]:mt-0">
              <MarkdownContent content={displayContentAbove} />
            </div>
          )}
          
          {/* Two-column layout for content BESIDE the image */}
          {(contentBeside || hasImage) && (
            <div className={`${hasImage ? 'grid lg:grid-cols-2 gap-8 items-start' : ''}`}>
              {/* Text column */}
              {contentBeside && (
                <div className={`prose prose-lg max-w-none text-muted-foreground [&>*:first-child]:mt-0 ${hasImage && imagePosition === 'left' ? 'lg:order-2' : ''}`}>
                  <MarkdownContent content={contentBeside} />
                  {/* CTA Buttons */}
                  <div className="mt-10 not-prose flex flex-wrap gap-3">
                    {ctaText && ctaLink && (
                      <Button asChild className="group">
                        <Link to={ctaLink}>
                          {ctaText}
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    )}
                    {phone && (
                      <Button variant="outline" asChild>
                        <a href={`tel:${phone.replace(/\s/g, '')}`}>
                          <Phone className="mr-2 h-4 w-4" />
                          {phone}
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              )}
              
              {/* Image column */}
              {hasImage && (
                <div className={`${imagePosition === 'left' ? 'lg:order-1' : ''}`}>
                  <picture>
                    <source 
                      srcSet={getResponsiveAvifSrcSet(image!)} 
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      type="image/avif" 
                    />
                    <source 
                      srcSet={getResponsiveWebpSrcSet(image!)} 
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      type="image/webp" 
                    />
                    <img 
                      src={getOptimizedImageUrl(image!, 'large')} 
                      srcSet={getResponsiveSrcSet(image!)}
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      alt={imageAlt || displayTitle || title}
                      width={600}
                      height={450}
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="w-full h-auto object-cover aspect-[4/3] rounded-lg shadow-md"
                    />
                  </picture>
                </div>
              )}
            </div>
          )}
        </article>
      </div>
    </section>
  );
};

export default HomepageIntroSection;
